package com.eladfinish.bible.contest.screens.search.suggestions

import com.eladfinish.bible.contest.screens.search.history.SearchHistoryRepository
import com.eladfinish.bible.contest.screens.search.history.SearchHistorySort
import com.eladfinish.contract.suggestions.SearchSuggestionEntry

/**
 * The autocomplete of the search screen. Everything runs on the device: the snapshot is in
 * memory, the history is local, and nothing typed is ever sent anywhere.
 */
class GetAutocompleteSuggestionsUseCase(
    private val popularSearchesRepository: PopularSearchesRepository,
    private val searchHistoryRepository: SearchHistoryRepository,
    private val configProvider: SearchSuggestionsConfigProvider,
) {

    /** False when the `disable_search_suggestions` kill switch is on. */
    val isEnabled: Boolean
        get() = !configProvider.config().isDisabled

    suspend fun forPrefix(prefix: String): List<AutocompleteSuggestion> {
        val config = configProvider.config()
        if (config.isDisabled || prefix.isEmpty()) return emptyList()

        return AutocompleteMatcher.forPrefix(
            prefix = prefix,
            personal = searchHistoryRepository.getHistory(SearchHistorySort.MOST_SEARCHED),
            snapshot = popularSearchesRepository.snapshot(),
            blocklist = config.blocklist,
            max = config.maxSuggestions,
        )
    }

    suspend fun popularLists(): PopularLists? {
        val config = configProvider.config()
        if (config.isDisabled) return null

        return AutocompleteMatcher.popularLists(
            snapshot = popularSearchesRepository.snapshot(),
            blocklist = config.blocklist,
            perGroup = POPULAR_LIST_ROWS,
        )
    }

    /** All the popular searches, for the "most searched" screen; blocked ones removed. */
    suspend fun popularSearches(): List<SearchSuggestionEntry> {
        val config = configProvider.config()
        if (config.isDisabled) return emptyList()

        return popularSearchesRepository.snapshot().entries.filter { it.q !in config.blocklist }
    }

    /** The "most searched" list, at most [limit] rows, blocked expressions removed. */
    suspend fun topList(
        filter: PopularSearchesFilter,
        limit: Int = TOP_LIST_SIZE,
    ): PopularSearchesList {
        val config = configProvider.config()
        val snapshot = popularSearchesRepository.snapshot()
        if (config.isDisabled) return PopularSearchesList(filter, emptyList(), snapshot.recentDays)

        val entries = when (filter) {
            PopularSearchesFilter.ALL_TIME ->
                snapshot.entries
                    .asSequence()
                    .filter { it.q !in config.blocklist }
                    .map { AutocompleteSuggestion(it.q, it.n, AutocompleteSuggestion.Origin.POPULAR) }
                    .take(limit)
                    .toList()

            PopularSearchesFilter.HOT_NOW ->
                snapshot.entries
                    .asSequence()
                    .filter { it.r > 0 && it.q !in config.blocklist }
                    .sortedWith(compareByDescending<SearchSuggestionEntry> { it.r }.thenByDescending { it.n })
                    .map { AutocompleteSuggestion(it.q, it.r, AutocompleteSuggestion.Origin.POPULAR) }
                    .take(limit)
                    .toList()

            PopularSearchesFilter.MINE ->
                searchHistoryRepository.getHistory(SearchHistorySort.MOST_SEARCHED)
                    .groupBy { it.key.query }
                    .map { (query, entries) ->
                        AutocompleteSuggestion(query, entries.sumOf { it.searchCount }, AutocompleteSuggestion.Origin.PERSONAL)
                    }
                    .sortedByDescending { it.count }
                    .take(limit)
        }

        return PopularSearchesList(filter = filter, entries = entries, recentDays = snapshot.recentDays)
    }

    suspend fun refreshIfStale() {
        if (configProvider.config().isDisabled) return

        popularSearchesRepository.refreshIfStale()
    }

    companion object {
        const val POPULAR_LIST_ROWS = 10
        const val TOP_LIST_SIZE = 100
    }
}
